import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { apiFetch } from '@/services/apiGateway';
import type { CSSProperties } from 'react';
import { GlobalHeader } from '@/components/GlobalHeader';

type Confidence = 'struggling' | 'on_track' | 'confident';

interface SubjectRow {
  icon: string;
  name: string;
  confidence: Confidence;
}

interface ConfidenceResponse {
  child_id: string;
  subjects: Record<string, string>;
}

interface TimetableEntry {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

const BUTTONS: { tKey: string; value: Confidence }[] = [
  { tKey: 'confidence.struggling', value: 'struggling' },
  { tKey: 'confidence.on_track',   value: 'on_track'   },
  { tKey: 'confidence.confident',  value: 'confident'  },
];

const ACTIVE_STYLES: Record<Confidence, CSSProperties> = {
  'struggling': { background: '#FEF2F2', color: 'var(--danger)',  borderColor: 'var(--danger)'  },
  'on_track':   { background: '#FEF3C7', color: 'var(--warning)', borderColor: 'var(--warning)' },
  'confident':  { background: '#F0FDF4', color: 'var(--success)', borderColor: 'var(--success)' },
};

const SUBJECT_ICONS: Record<string, string> = {
  Mathematics: '🧮', Maths: '🧮', Math: '🧮',
  History: '📜',
  Physics: '⚗️',
  Chemistry: '🧪',
  Biology: '🧬',
  English: '📖',
  Geography: '🌍',
  French: '🇫🇷',
  Spanish: '🇪🇸',
  German: '🇩🇪',
  'Computer Science': '💻',
  Music: '🎵',
  Art: '🎨',
  Philosophy: '🤔',
  Economics: '📊',
};

export function ConfidencePage() {
  const { t } = useTranslation();
  const { childId } = useAuthStore();
  const [confidences, setConfidences] = useState<SubjectRow[]>([]);

  const { data: confidenceData, isLoading: confLoading } = useData<ConfidenceResponse>(
    childId ? `/children/${childId}/confidence` : null,
  );
  const { data: timetableData, isLoading: ttLoading } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );

  const isLoading = confLoading || ttLoading;

  useEffect(() => {
    if (confLoading || ttLoading) return;

    const rated = confidenceData?.subjects ?? {};

    // Unique subjects from timetable, preserving insertion order
    const timetableSubjects = Array.from(
      new Set((timetableData?.entries ?? []).map((e) => e.subject)),
    );

    // Merge: timetable subjects first (so unrated ones appear), then any rated subjects
    // not in the timetable (e.g. from a previous timetable)
    const allSubjects = Array.from(
      new Set([...timetableSubjects, ...Object.keys(rated)]),
    );

    setConfidences(
      allSubjects.map((subject) => ({
        icon: SUBJECT_ICONS[subject] ?? '📚',
        name: subject,
        confidence: (rated[subject] as Confidence | undefined) ?? 'on_track',
      })),
    );
  }, [confidenceData, timetableData, confLoading, ttLoading]);

  const update = (subject: string, value: Confidence) => {
    // Compute next state, then use it for both local update and PUT body
    const next = confidences.map((s) => (s.name === subject ? { ...s, confidence: value } : s));
    setConfidences(next);
    // PUT the full subjects dict (backend replaces entire map)
    if (childId) {
      const subjects = Object.fromEntries(next.map((s) => [s.name, s.confidence]));
      apiFetch(`/children/${childId}/confidence`, {
        method: 'PUT',
        body: JSON.stringify({ subjects }),
      }).catch(console.error);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student/progress" />

        <div className="app-content">

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
            {t('confidence.title')}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 24px', lineHeight: 1.5 }}>
            {t('confidence.desc')}
          </p>

          {/* Loading skeletons */}
          {isLoading && [1, 2, 3].map((i) => (
            <div key={i} className="loading-skeleton" style={{ height: '96px', borderRadius: '12px' }} />
          ))}

          {/* Subject rows from API */}
          {!isLoading && confidences.map((subject) => (
            <div key={subject.name} className="subject-row">
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-dark)', margin: 0 }}>
                {subject.icon} {subject.name}
              </h3>
              <div className="toggle-group">
                {BUTTONS.map((btn) => (
                  <button
                    key={btn.value}
                    type="button"
                    onClick={() => update(subject.name, btn.value)}
                    style={subject.confidence === btn.value ? ACTIVE_STYLES[btn.value] : undefined}
                  >
                    {t(btn.tKey)}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Empty state */}
          {!isLoading && confidences.length === 0 && (
            <div className="card" style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
                {t('confidence.no_subjects')}
              </p>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
